import { lineString } from '@turf/helpers';
import pointToLineDistance from '@turf/point-to-line-distance';
import { DateTime } from 'luxon';
import { capitalize, readonly } from 'vue';

import { Unit } from '@/common/models';
import Status from '@/common/models/Status';
import { DASH, uniq } from '@/globals';
import UserNotifications from '@/utils/UserNotifications';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

// API Objects
export const journeyStatuses = ['SUBMITTED', 'EXPIRED', 'ERROR', 'CANCELLED', 'COMPLETED', 'RUNNING'] as const;
type JourneyStatus = (typeof journeyStatuses)[number];
interface IPostalDataAPI {
  geofence_id: number;
  postal_code: string;
  count: number;
  is_selected: boolean;
  unit_ids: number[];
}
interface IJourneyAnalysisAPI {
  geofence_group_id: number;
  geofencegroup_ids?: number[];
  journey_name: string;
  created_on_utc: string;
  modified_on_utc?: string;
  postal_data: IPostalDataAPI[];
  workflowconfiguration_id: number;
  workflowexecution_id?: number;
  workflowexecution_state?: JourneyStatus;
}

// Helpers
export interface Point {
  lng: number;
  lat: number;
}
export interface Route {
  bounds: { min: Point; max: Point };
  path: Point[];
}
export class PostalData {
  public routes: Route[] = [];
  public rank: number;
  public percentage: number;

  static readonly MAX_UNIT_DISTANCE = 50;

  constructor(
    public geofenceID: number,
    public postalCode: string,
    public readonly count: number,
    public isSelected: boolean,
    public unitIDs: number[] = [],
  ) {}

  static fromApi(apiObject: IPostalDataAPI) {
    return new PostalData(
      apiObject.geofence_id,
      apiObject.postal_code,
      apiObject.count,
      apiObject.is_selected,
      apiObject.unit_ids,
    );
  }

  clone() {
    const data = new PostalData(this.geofenceID, this.postalCode, this.count, this.isSelected, this.unitIDs);
    data.routes = this.routes;
    data.rank = this.rank;
    data.percentage = this.percentage;
    return data;
  }

  toApi(): IPostalDataAPI {
    return {
      geofence_id: this.geofenceID,
      postal_code: this.postalCode,
      count: this.count,
      is_selected: this.isSelected,
      unit_ids: this.isSelected ? this.unitIDs : [],
    };
  }

  equals(other: PostalData) {
    return this.geofenceID === other.geofenceID && this.postalCode === other.postalCode;
  }

  addUnitsAlongRoute(units: Unit[]) {
    if (!this.routes) return;
    const routeLines = this.routes.map((route) => {
      const coordinates = route.path.map((p) => [p.lat, p.lng]);
      return lineString(coordinates);
    });
    this.unitIDs = units
      .filter((u) => {
        const coordinate = [u.latitude, u.longitude];
        return routeLines.some((line) => {
          const dist = pointToLineDistance(coordinate, line, { units: 'meters' });
          return dist <= PostalData.MAX_UNIT_DISTANCE;
        });
      })
      .map((u) => u.id);
    if (!this.unitIDs.length) {
      UserNotifications.warning({ title: null, message: capitalize(i18n.global.t('messages.noUnitsNearJourney')) });
    }
  }
}

export default class JourneyAnalysis extends Validatable<JourneyAnalysis> {
  private postalDataForGeofence: Record<number, PostalData[]> = {};
  private selectedPostalData: PostalData[] = [];

  constructor(
    public id: number = -1,
    public geofenceGroupID: number = -1,
    public workflowExecutionID: number = -1,
    public name: string = '',
    public createdOnUTC: DateTime = DateTime.now(),
    public modifiedOnUTC: DateTime = DateTime.now(),
    private postalData: PostalData[] = [],
    public status: JourneyStatus = 'SUBMITTED',
  ) {
    super();
    // Construct map from geofence -> data
    postalData.forEach((pd) => {
      if (!this.postalDataForGeofence[pd.geofenceID]) {
        this.postalDataForGeofence[pd.geofenceID] = [];
      }
      this.postalDataForGeofence[pd.geofenceID].push(pd);
    });
    // Construct selected postal data
    this.selectedPostalData = postalData.filter((x) => x.isSelected);
    // Populate postal data info
    const geofenceIDs = this.geofenceIDs;
    geofenceIDs.forEach((id) => {
      const geofenceData = this.forGeofenceID(id);
      const sum = geofenceData.reduce((result, data) => result + data.count, 0);
      geofenceData
        .sort((a, b) => b.count - a.count)
        .forEach((gd, i) => {
          gd.rank = i + 1;
          gd.percentage = gd.count / sum;
        });
    });
  }

  get createdOnFormatted() {
    return this.createdOnUTC ? this.createdOnUTC.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted() {
    return this.modifiedOnUTC ? this.modifiedOnUTC.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get statusSimple(): Exclude<Status, 'pendingApproval'> {
    if (this.status === 'COMPLETED') return 'completed';
    else if (this.status === 'ERROR' || this.status === 'EXPIRED' || this.status === 'CANCELLED') return 'errored';
    else if (this.status === 'RUNNING') return 'running';
    return 'upcoming';
  }

  get data() {
    return readonly(this.postalData);
  }

  get selectedData() {
    return readonly(this.selectedPostalData);
  }

  get geofenceIDs() {
    return uniq(this.postalData.map((x) => x.geofenceID));
  }

  get postalCodes(): string[] {
    return uniq(this.postalData.map((x) => x.postalCode));
  }

  get selectedGeofenceIDs(): number[] {
    return uniq(this.selectedPostalData.map((x) => x.geofenceID));
  }

  get selectedPostalCodes(): string[] {
    return uniq(this.selectedPostalData.map((x) => x.postalCode));
  }

  get selectedUnitIDs(): number[] {
    return uniq(
      this.selectedPostalData.reduce((r: number[], x) => {
        return [...r, ...x.unitIDs];
      }, []),
    );
  }

  get isEmpty() {
    return !this.selectedUnitIDs.length;
  }

  /* Get Methods */

  forGeofenceID(geofenceID: number): PostalData[] {
    return this.postalDataForGeofence[geofenceID] ?? [];
  }

  unitsForGeofenceID(geofenceID: number): number[] {
    return uniq(
      this.forGeofenceID(geofenceID).reduce((r: number[], x) => {
        return [...r, ...x.unitIDs];
      }, []),
    );
  }

  findPostalData(geofenceID: number, postalCode: string) {
    return this.postalData.find((x) => x.geofenceID === geofenceID && x.postalCode === postalCode);
  }

  /* Update Methods */

  selectPostalData(data: PostalData, selected: boolean = true) {
    data.isSelected = selected;
    if (selected) {
      this.selectedPostalData.push(data);
    } else {
      const idx = this.selectedPostalData.find(
        (x) => x.geofenceID === data.geofenceID && x.postalCode === data.postalCode,
      );
      this.selectedPostalData.splice(idx, 1);
    }
    this.postalDataChange(data);
  }

  updatePostalData(data: PostalData, routes: Route[], units?: Unit[]) {
    data.routes = routes;
    if (units) {
      data.addUnitsAlongRoute(units);
    }
    this.postalDataChange(data);
  }

  clearPostalData(data: PostalData) {
    data.routes = [];
    data.unitIDs = [];
    this.postalDataChange(data);
  }

  private postalDataChange(data: PostalData) {
    const idxMain = this.postalData.findIndex(
      (x) => x.geofenceID === data.geofenceID && x.postalCode === data.postalCode,
    );
    if (idxMain >= 0) {
      this.postalData.splice(idxMain, 1, data);
    }
    const idxGeofence = this.postalDataForGeofence[data.geofenceID].findIndex((x) => x.postalCode === data.postalCode);
    if (idxGeofence >= 0) {
      this.postalDataForGeofence[data.geofenceID].splice(idxGeofence, 1, data);
    }
    const idxSelected = this.selectedPostalData.findIndex(
      (x) => x.geofenceID === data.geofenceID && x.postalCode === data.postalCode,
    );
    if (idxSelected >= 0) {
      this.selectedPostalData.splice(idxSelected, 1, data);
    }
  }

  static fromObject(obj: object) {
    return super.fromObject(obj, new JourneyAnalysis());
  }

  static fromApi(apiObject: IJourneyAnalysisAPI) {
    return new JourneyAnalysis(
      apiObject.workflowconfiguration_id,
      apiObject.geofence_group_id,
      apiObject.workflowexecution_id,
      apiObject.journey_name,
      apiObject.created_on_utc ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      apiObject.modified_on_utc ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      apiObject.postal_data?.map((x) => PostalData.fromApi(x)) ?? [],
      apiObject.workflowexecution_state,
    );
  }

  toApi(): IJourneyAnalysisAPI {
    return {
      workflowconfiguration_id: this.id,
      workflowexecution_id: this.workflowExecutionID,
      geofence_group_id: this.geofenceGroupID,
      geofencegroup_ids: [this.geofenceGroupID],
      journey_name: this.name,
      created_on_utc: this.createdOnUTC.toISO(),
      modified_on_utc: this.modifiedOnUTC.toISO(),
      postal_data: this.postalData?.map((x) => x.toApi()) ?? [],
      workflowexecution_state: this.status,
    };
  }
}
